import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  /** Klartext-Erläuterung des Fachbegriffs, direkt am Feld. */
  hinweis?: ReactNode;
  fehler?: string;
  /** Einheit rechts im Feld, z. B. „€“ oder „m²“. */
  einheit?: string;
  labelVerbergen?: boolean;
}

const FELD_BASIS =
  'w-full rounded-lg border bg-surface px-3 text-sm text-fg placeholder:text-fg-subtle ' +
  'transition-colors duration-fast ease-out ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
  'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-subtle';

export function Input({
  label,
  hinweis,
  fehler,
  einheit,
  labelVerbergen = false,
  id,
  className,
  ...rest
}: InputProps) {
  const eigeneId = useId();
  const feldId = id ?? eigeneId;
  const hinweisId = `${feldId}-hinweis`;
  const fehlerId = `${feldId}-fehler`;

  return (
    <div className="min-w-0">
      <label
        htmlFor={feldId}
        className={cn(
          'mb-1.5 block text-sm font-medium text-fg',
          labelVerbergen && 'sr-only',
        )}
      >
        {label}
      </label>

      <div className="relative">
        <input
          id={feldId}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={cn(hinweis ? hinweisId : undefined, fehler ? fehlerId : undefined) || undefined}
          className={cn(
            FELD_BASIS,
            'h-11',
            einheit && 'pr-12',
            fehler ? 'border-danger' : 'border-border-strong hover:border-fg-subtle',
            className,
          )}
          {...rest}
        />
        {einheit && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-fg-subtle">
            {einheit}
          </span>
        )}
      </div>

      {hinweis && !fehler && (
        <p id={hinweisId} className="mt-1.5 text-xs leading-relaxed text-fg-muted">
          {hinweis}
        </p>
      )}
      {fehler && (
        <p id={fehlerId} className="mt-1.5 text-xs font-medium text-danger">
          {fehler}
        </p>
      )}
    </div>
  );
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  hinweis?: ReactNode;
  fehler?: string;
  labelVerbergen?: boolean;
}

export function Select({
  label,
  hinweis,
  fehler,
  labelVerbergen = false,
  id,
  className,
  children,
  ...rest
}: SelectProps) {
  const eigeneId = useId();
  const feldId = id ?? eigeneId;
  const hinweisId = `${feldId}-hinweis`;
  const fehlerId = `${feldId}-fehler`;

  return (
    <div className="min-w-0">
      <label
        htmlFor={feldId}
        className={cn('mb-1.5 block text-sm font-medium text-fg', labelVerbergen && 'sr-only')}
      >
        {label}
      </label>
      <div className="relative">
        <select
          id={feldId}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={
            cn(hinweis ? hinweisId : undefined, fehler ? fehlerId : undefined) || undefined
          }
          className={cn(
            FELD_BASIS,
            'h-11 appearance-none pr-9',
            fehler ? 'border-danger' : 'border-border-strong hover:border-fg-subtle',
            className,
          )}
          {...rest}
        >
          {children}
        </select>
        {/* Auswahlpfeil folgt der Textfarbe aus den Tokens. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-fg-subtle"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </div>
      {hinweis && !fehler && (
        <p id={hinweisId} className="mt-1.5 text-xs leading-relaxed text-fg-muted">
          {hinweis}
        </p>
      )}
      {fehler && (
        <p id={fehlerId} className="mt-1.5 text-xs font-medium text-danger">
          {fehler}
        </p>
      )}
    </div>
  );
}
